"use client";

import { Suspense, type ReactNode } from "react";
import { AuthModalProvider } from "../hooks/useAuthModal";
import { PricingModalProvider } from "../hooks/usePricingModal";
import { WelcomeModalProvider } from "../hooks/useWelcomeModal";
import { UpgradeSuccessModalProvider } from "../hooks/useUpgradeSuccessModal";
import { UserProvider, type ServerAuthHint } from "../hooks/useUser";
import { WatchlistProvider } from "@/features/watchlist/hooks/useWatchlist";
import { AuthModal } from "./AuthModal";
import { GlobalPricingModal } from "./GlobalPricingModal";
import { WelcomeModal } from "./WelcomeModal";
import { WelcomeModalFromUrl } from "./WelcomeModalFromUrl";
import { UpgradeSuccessModal } from "./UpgradeSuccessModal";
import { UpgradeSuccessFromUrl } from "./UpgradeSuccessFromUrl";
import { GoogleOneTap } from "./GoogleOneTap";
import { SubscriptionNoticeBanner } from "./SubscriptionNoticeBanner";
import { AnalyticsProvider } from "@/components/analytics/AnalyticsProvider";
import { BillingProvider } from "@/features/billing/hooks/useBilling";

type AuthProvidersProps = {
  children: ReactNode;
  serverHint?: ServerAuthHint | null;
};

export function AuthProviders({ children, serverHint }: AuthProvidersProps) {
  return (
    <UserProvider serverHint={serverHint}>
      <WatchlistProvider>
        <AuthModalProvider>
          <PricingModalProvider>
            <WelcomeModalProvider>
              <UpgradeSuccessModalProvider>
                {/* Billing actions (checkout / plan change / cancel) need the
                    user, the auth modal and the success modal — hence here. */}
                <BillingProvider>
                  {children}
                  <AuthModal />
                  <GlobalPricingModal />
                  <WelcomeModal />
                  <UpgradeSuccessModal />
                  <Suspense fallback={null}>
                    <WelcomeModalFromUrl />
                    <UpgradeSuccessFromUrl />
                  </Suspense>
                  <GoogleOneTap />
                  <SubscriptionNoticeBanner />
                  {/* Reads useUser for identity; loads posthog-js on idle. */}
                  <AnalyticsProvider />
                </BillingProvider>
              </UpgradeSuccessModalProvider>
            </WelcomeModalProvider>
          </PricingModalProvider>
        </AuthModalProvider>
      </WatchlistProvider>
    </UserProvider>
  );
}
